import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import moment from "moment-timezone";
import { reservationAmendmentService } from "../../_services";

Modal.setAppElement("#root");

const WORKFLOW_VERSION = 1;
const WORKFLOW_PREFIX = "crewmates.admin.reservation-amendment.v1";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const FINGERPRINT_PATTERN = /^[a-f0-9]{64}$/;
const OPERATION_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,254}$/;
const ACTIVE_STATES = new Set([
  "pending",
  "processing_refund",
  "finalizing",
  "retryable_failed",
  "reconciling",
]);
const AMENDMENT_STATES = new Set([
  ...ACTIVE_STATES,
  "succeeded",
  "terminal_failed",
]);
const CLASSIFICATIONS = new Set([
  "no_change",
  "mixed",
  "pure_extension",
  "pure_shortening",
]);
const FINANCIAL_ACTIONS = new Set([
  "no_change",
  "exact_refund",
  "customer_authorization_required",
  "invoice_workflow_required",
  "unsupported",
]);
const POLL_DELAY_MS = 60000;
const RETRYABLE_RESUME_DELAY_MS = 15 * 60 * 1000;
const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 1000;
const MAX_SERVICE_DAYS = 1095;
const AUTOMATIC_REPLAY_STATES = new Set([
  "pending",
  "processing_refund",
  "finalizing",
]);
const SAFE_PRE_OPERATION_REJECTION_CODES = new Set([
  "amendment_quote_changed",
  "amendment_dates_unavailable",
  "amendment_financial_action_unsupported",
  "amendment_not_committable",
  "amendment_booking_changed",
  "amendment_booking_missing",
  "amendment_booking_state_unsupported",
  "amendment_open_ended_unsupported",
]);

const modalStyles = {
  overlay: {
    zIndex: 7,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "min(840px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    transform: "translate(-50%, -50%)",
  },
};

function isValidDateOnly(value) {
  if (typeof value !== "string" || !DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function dateOnly(value, timezone = null) {
  if (typeof value !== "string") return "";
  if (
    value.includes("T") &&
    typeof timezone === "string" &&
    moment.tz.zone(timezone) &&
    moment(value).isValid()
  ) {
    return moment(value).tz(timezone).format("YYYY-MM-DD");
  }
  const normalized = value.includes("T") ? value.split("T")[0] : value;
  return isValidDateOnly(normalized) ? normalized : "";
}

function validDateList(value, { allowEmpty = true } = {}) {
  if (!Array.isArray(value) || (!allowEmpty && value.length === 0)) return null;
  if (
    value.length > MAX_SERVICE_DAYS ||
    value.some((entry) => !isValidDateOnly(entry))
  ) {
    return null;
  }
  const normalized = [...value].sort();
  return new Set(normalized).size === normalized.length ? normalized : null;
}

function dateOrdinal(value) {
  if (!isValidDateOnly(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  return date.getTime() / (24 * 60 * 60 * 1000);
}

function serviceDateRange(checkInDate, checkOutDate) {
  const start = dateOrdinal(checkInDate);
  const end = dateOrdinal(checkOutDate);
  const count = end === null || start === null ? null : end - start;
  if (!Number.isSafeInteger(count) || count <= 0 || count > MAX_SERVICE_DAYS) {
    return null;
  }
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(0);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCFullYear(1970, 0, 1 + start + index);
    return date.toISOString().slice(0, 10);
  });
}

function sameDateList(left, right) {
  return Boolean(
    Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((date, index) => date === right[index]),
  );
}

function dateChangeMatches(source) {
  const previous = serviceDateRange(
    source && source.previousCheckInDate,
    source && source.previousCheckOutDate,
  );
  const proposed = serviceDateRange(
    source && source.proposedCheckInDate,
    source && source.proposedCheckOutDate,
  );
  if (!previous || !proposed) return false;
  const previousSet = new Set(previous);
  const proposedSet = new Set(proposed);
  const removed = previous.filter((date) => !proposedSet.has(date));
  const added = proposed.filter((date) => !previousSet.has(date));
  const retained = proposed.filter((date) => previousSet.has(date));
  let classification = "mixed";
  if (removed.length === 0 && added.length === 0) classification = "no_change";
  else if (removed.length > 0 && added.length === 0)
    classification = "pure_shortening";
  else if (removed.length === 0 && added.length > 0)
    classification = "pure_extension";
  return Boolean(
    source.classification === classification &&
      (!source.previousServiceDates ||
        sameDateList(source.previousServiceDates, previous)) &&
      (!source.proposedServiceDates ||
        sameDateList(source.proposedServiceDates, proposed)) &&
      sameDateList(source.removedServiceDates, removed) &&
      sameDateList(source.addedServiceDates, added) &&
      sameDateList(source.retainedServiceDates, retained)
  );
}

function validMoney(
  value,
  { positive = false, nonNegative = false } = {},
) {
  return (
    Number.isSafeInteger(value) &&
    (positive ? value > 0 : true) &&
    (nonNegative ? value >= 0 : true) &&
    Math.abs(value) <= Number.MAX_SAFE_INTEGER
  );
}

function validAvailabilityEntry(entry) {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return false;
  const optionalStrings = ["id", "bookingId", "status", "type", "source"];
  if (
    optionalStrings.some(
      (field) =>
        entry[field] !== undefined &&
        entry[field] !== null &&
        typeof entry[field] !== "string",
    )
  ) {
    return false;
  }
  const optionalDates = [
    "checkInDate",
    "checkOutDate",
    "serviceDate",
    "throughDate",
  ];
  return optionalDates.every(
    (field) =>
      entry[field] === undefined ||
      entry[field] === null ||
      isValidDateOnly(entry[field]),
  );
}

function workflowKey(bookingId) {
  return `${WORKFLOW_PREFIX}:${bookingId}`;
}

function validatedWorkflow(value, bookingId) {
  const lastKnownState = value && value.lastKnownState;
  const resumeAfter =
    value && value.resumeAfter !== undefined ? value.resumeAfter : 0;
  if (
    !value ||
    value.version !== WORKFLOW_VERSION ||
    value.bookingId !== bookingId ||
    !isValidDateOnly(value.proposedCheckInDate) ||
    !isValidDateOnly(value.proposedCheckOutDate) ||
    value.proposedCheckInDate >= value.proposedCheckOutDate ||
    typeof value.reason !== "string" ||
    value.reason.trim() !== value.reason ||
    value.reason.length < MIN_REASON_LENGTH ||
    value.reason.length > MAX_REASON_LENGTH ||
    value.noticeWaiver !== true ||
    !FINGERPRINT_PATTERN.test(value.quoteFingerprint || "") ||
    !OPERATION_KEY_PATTERN.test(value.idempotencyKey || "") ||
    (value.amendmentId !== null &&
      value.amendmentId !== undefined &&
      !OBJECT_ID_PATTERN.test(value.amendmentId || "")) ||
    (lastKnownState !== null &&
      lastKnownState !== undefined &&
      !ACTIVE_STATES.has(lastKnownState)) ||
    !Number.isSafeInteger(resumeAfter) ||
    resumeAfter < 0 ||
    (value.requiresManualReconciliation !== undefined &&
      typeof value.requiresManualReconciliation !== "boolean")
  ) {
    return null;
  }
  return {
    version: WORKFLOW_VERSION,
    bookingId,
    proposedCheckInDate: value.proposedCheckInDate,
    proposedCheckOutDate: value.proposedCheckOutDate,
    reason: value.reason,
    noticeWaiver: true,
    quoteFingerprint: value.quoteFingerprint,
    idempotencyKey: value.idempotencyKey,
    amendmentId: value.amendmentId || null,
    lastKnownState: lastKnownState || null,
    requiresManualReconciliation:
      value.requiresManualReconciliation === true,
    resumeAfter,
  };
}

function readWorkflow(bookingId) {
  if (!bookingId || typeof window === "undefined") return null;
  try {
    const storage = window.sessionStorage;
    if (!storage) return null;
    const key = workflowKey(bookingId);
    const parsed = JSON.parse(storage.getItem(key) || "null");
    const workflow = validatedWorkflow(parsed, bookingId);
    if (!workflow && parsed) storage.removeItem(key);
    return workflow;
  } catch (error) {
    return null;
  }
}

function persistWorkflow(workflow) {
  const validated = validatedWorkflow(workflow, workflow && workflow.bookingId);
  if (!validated || typeof window === "undefined") return false;
  try {
    const storage = window.sessionStorage;
    if (!storage) return false;
    storage.setItem(workflowKey(validated.bookingId), JSON.stringify(validated));
    return true;
  } catch (error) {
    return false;
  }
}

function removeWorkflow(bookingId) {
  if (!bookingId || typeof window === "undefined") return;
  try {
    const storage = window.sessionStorage;
    if (storage) storage.removeItem(workflowKey(bookingId));
  } catch (error) {
    // A storage failure cannot authorize a second request; the saved key remains authoritative.
  }
}

function quoteMatchesRequest(quote, request) {
  const availability = quote && quote.availability;
  const arraysValid = [
    quote && quote.previousServiceDates,
    quote && quote.proposedServiceDates,
    quote && quote.removedServiceDates,
    quote && quote.addedServiceDates,
    quote && quote.retainedServiceDates,
  ].every((dates) => validDateList(dates));
  return Boolean(
    quote &&
      quote.schemaVersion === WORKFLOW_VERSION &&
      String(quote.bookingId || "") === String(request.bookingId || "") &&
      quote.currency === "usd" &&
      typeof quote.timezone === "string" &&
      quote.timezone.length > 0 &&
      typeof quote.asOf === "string" &&
      !Number.isNaN(Date.parse(quote.asOf)) &&
      quote.proposedCheckInDate === request.proposedCheckInDate &&
      quote.proposedCheckOutDate === request.proposedCheckOutDate &&
      quote.reason === request.reason &&
      quote.noticeWaiver === true &&
      CLASSIFICATIONS.has(quote.classification) &&
      FINANCIAL_ACTIONS.has(quote.financialAction) &&
      dateChangeMatches(quote) &&
      typeof quote.canCommit === "boolean" &&
      typeof quote.quoteFingerprint === "string" &&
      FINGERPRINT_PATTERN.test(quote.quoteFingerprint) &&
      availability &&
      typeof availability.available === "boolean" &&
      Number.isSafeInteger(availability.candidateCount) &&
      availability.candidateCount >= 0 &&
      Array.isArray(availability.conflicts) &&
      Array.isArray(availability.manualBlocks) &&
      availability.conflicts.every(validAvailabilityEntry) &&
      availability.manualBlocks.every(validAvailabilityEntry) &&
      arraysValid &&
      validMoney(quote.guestPriceDifferenceCents) &&
      validMoney(quote.guestRefundCents, { nonNegative: true }) &&
      validMoney(quote.hostDebitCents, { nonNegative: true }) &&
      validMoney(quote.preventedFutureHostEarningsCents, {
        nonNegative: true,
      }) &&
      validMoney(quote.platformRevenueReversalCents, { nonNegative: true })
  );
}

function quoteCanCommit(quote) {
  return Boolean(
    quote &&
      quote.canCommit === true &&
      quote.classification === "pure_shortening" &&
      quote.financialAction === "exact_refund" &&
      quote.availability &&
      quote.availability.available === true &&
      validMoney(quote.guestRefundCents, { positive: true }) &&
      quote.guestPriceDifferenceCents === -quote.guestRefundCents &&
      quote.guestRefundCents ===
        quote.hostDebitCents +
          quote.preventedFutureHostEarningsCents +
          quote.platformRevenueReversalCents &&
      validDateList(quote.removedServiceDates, { allowEmpty: false }) &&
      validDateList(quote.addedServiceDates) &&
      quote.addedServiceDates.length === 0 &&
      validDateList(quote.retainedServiceDates, { allowEmpty: false })
  );
}

function validAmendment(amendment) {
  return Boolean(
    amendment &&
      OBJECT_ID_PATTERN.test(String(amendment.id || "")) &&
      OBJECT_ID_PATTERN.test(String(amendment.bookingId || "")) &&
      AMENDMENT_STATES.has(amendment.state) &&
      amendment.classification === "pure_shortening" &&
      dateChangeMatches(amendment) &&
      amendment.financialAction === "exact_refund" &&
      amendment.currency === "usd" &&
      amendment.noticeWaiver === true &&
      typeof amendment.reason === "string" &&
      FINGERPRINT_PATTERN.test(amendment.quoteFingerprint || "") &&
      isValidDateOnly(amendment.proposedCheckInDate) &&
      isValidDateOnly(amendment.proposedCheckOutDate) &&
      amendment.proposedCheckInDate < amendment.proposedCheckOutDate &&
      validDateList(amendment.removedServiceDates, { allowEmpty: false }) &&
      validDateList(amendment.addedServiceDates) &&
      amendment.addedServiceDates.length === 0 &&
      validDateList(amendment.retainedServiceDates, { allowEmpty: false }) &&
      validMoney(amendment.guestRefundCents, { positive: true }) &&
      amendment.guestPriceDifferenceCents === -amendment.guestRefundCents &&
      validMoney(amendment.hostDebitCents, { nonNegative: true }) &&
      validMoney(amendment.preventedFutureHostEarningsCents, {
        nonNegative: true,
      }) &&
      validMoney(amendment.platformRevenueReversalCents, {
        nonNegative: true,
      }) &&
      amendment.guestRefundCents ===
        amendment.hostDebitCents +
          amendment.preventedFutureHostEarningsCents +
          amendment.platformRevenueReversalCents
  );
}

function amendmentMatchesWorkflow(amendment, workflow) {
  return Boolean(
    validAmendment(amendment) &&
      String(amendment.bookingId) === workflow.bookingId &&
      amendment.proposedCheckInDate === workflow.proposedCheckInDate &&
      amendment.proposedCheckOutDate === workflow.proposedCheckOutDate &&
      amendment.reason === workflow.reason &&
      amendment.quoteFingerprint === workflow.quoteFingerprint &&
      (!workflow.amendmentId || amendment.id === workflow.amendmentId)
  );
}

function stateLabel(amendment) {
  switch (amendment && amendment.state) {
    case "pending":
      return "Queued";
    case "processing_refund":
      return "Processing refund";
    case "finalizing":
      return "Updating reservation";
    case "retryable_failed":
      return "Retry scheduled";
    case "reconciling":
      return "Manual reconciliation";
    case "succeeded":
      return "Completed";
    case "terminal_failed":
      return "Failed";
    default:
      return "Unknown";
  }
}

function actionLabel(action) {
  switch (action) {
    case "exact_refund":
      return "Refund removed dates";
    case "customer_authorization_required":
      return "Customer authorization required";
    case "invoice_workflow_required":
      return "Invoice or payment request required";
    case "unsupported":
      return "Unsupported in this workflow";
    case "no_change":
      return "No financial action";
    default:
      return "Not calculated";
  }
}

function formatCents(value, currency = "usd") {
  if (!Number.isSafeInteger(value)) return "Unavailable";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
    }).format(value / 100);
  } catch (error) {
    return `${(value / 100).toFixed(2)} ${String(currency || "usd").toUpperCase()}`;
  }
}

function dateListLabel(dates) {
  return Array.isArray(dates) && dates.length > 0 ? dates.join(", ") : "None";
}

function errorMessage(error, fallback) {
  const code = error && error.code ? ` (${error.code})` : "";
  return `${(error && error.message) || fallback}${code}`;
}

function resumeDelayForState(state) {
  return state === "retryable_failed"
    ? RETRYABLE_RESUME_DELAY_MS
    : POLL_DELAY_MS;
}

function waitLabel(milliseconds) {
  const seconds = Math.max(1, Math.ceil(milliseconds / 1000));
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

function requestFromState(bookingId, checkInDate, checkOutDate, reason, noticeWaiver) {
  const normalizedReason = typeof reason === "string" ? reason.trim() : "";
  if (!bookingId) return { error: "A reservation is required." };
  if (!isValidDateOnly(checkInDate) || !isValidDateOnly(checkOutDate)) {
    return { error: "Enter valid check-in and check-out dates." };
  }
  if (checkInDate >= checkOutDate) {
    return { error: "Check-out must be after check-in." };
  }
  if (
    normalizedReason.length < MIN_REASON_LENGTH ||
    normalizedReason.length > MAX_REASON_LENGTH
  ) {
    return {
      error: `Reason must be ${MIN_REASON_LENGTH}-${MAX_REASON_LENGTH} characters.`,
    };
  }
  if (noticeWaiver !== true) {
    return {
      error: "Confirm that the guest and host agreed to the date change.",
    };
  }
  return {
    value: {
      bookingId,
      proposedCheckInDate: checkInDate,
      proposedCheckOutDate: checkOutDate,
      reason: normalizedReason,
      noticeWaiver: true,
    },
  };
}

function InfoRow({ label, value, warning = false }) {
  return (
    <div className="tw-flex tw-justify-between tw-gap-4 tw-border-b tw-border-gray-100 tw-py-2">
      <span className="tw-text-sm tw-text-gray-600">{label}</span>
      <span
        className={`tw-text-right tw-text-sm tw-font-semibold ${
          warning ? "tw-text-red-700" : "tw-text-gray-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function ReservationAmendmentModal({
  isOpen,
  bookingId,
  reservationId,
  currentCheckInDate,
  currentCheckOutDate,
  bookingStatus,
  bookingTimezone,
  bookingType,
  onClose,
  onCompleted,
}) {
  const initialCheckIn = dateOnly(currentCheckInDate, bookingTimezone);
  const initialCheckOut = dateOnly(currentCheckOutDate, bookingTimezone);
  const [proposedCheckInDate, setProposedCheckInDate] = useState(initialCheckIn);
  const [proposedCheckOutDate, setProposedCheckOutDate] = useState(initialCheckOut);
  const [reason, setReason] = useState("");
  const [noticeWaiver, setNoticeWaiver] = useState(false);
  const [quote, setQuote] = useState(null);
  const [amendment, setAmendment] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canClearRejectedWorkflow, setCanClearRejectedWorkflow] = useState(false);
  const [resumeClock, setResumeClock] = useState(() => Date.now());
  const contextRef = useRef(0);
  const quoteRequestRef = useRef(0);
  const submitLockRef = useRef(false);
  const pollTimerRef = useRef(null);
  const resumeUnlockTimerRef = useRef(null);
  const runWorkflowRef = useRef(null);
  const completedIdRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const clearResumeUnlock = useCallback(() => {
    if (resumeUnlockTimerRef.current) {
      clearTimeout(resumeUnlockTimerRef.current);
      resumeUnlockTimerRef.current = null;
    }
  }, []);

  const isCurrentContext = useCallback(
    (token, expectedBookingId) =>
      contextRef.current === token &&
      isOpen &&
      String(bookingId || "") === String(expectedBookingId || ""),
    [bookingId, isOpen],
  );

  const scheduleReplay = useCallback((savedWorkflow, currentAmendment, token) => {
    clearPoll();
    if (
      !AUTOMATIC_REPLAY_STATES.has(currentAmendment.state) ||
      currentAmendment.requiresManualReconciliation === true
    ) {
      return;
    }
    pollTimerRef.current = setTimeout(() => {
      if (runWorkflowRef.current) {
        runWorkflowRef.current(savedWorkflow, token, { automatic: true });
      }
    }, Math.max(0, savedWorkflow.resumeAfter - Date.now()));
  }, [clearPoll]);

  const runWorkflow = useCallback(
    async (savedWorkflow, token, { automatic = false } = {}) => {
      if (!isCurrentContext(token, savedWorkflow.bookingId)) return;
      clearPoll();
      submitLockRef.current = true;
      setSubmitting(true);
      setCanClearRejectedWorkflow(false);
      setError("");
      setMessage(
        automatic
          ? "Refreshing the saved amendment request..."
          : "Submitting the saved amendment request...",
      );
      try {
        const result = await reservationAmendmentService.commit(savedWorkflow);
        if (!isCurrentContext(token, savedWorkflow.bookingId)) return;
        const nextAmendment = result && result.amendment;
        if (!amendmentMatchesWorkflow(nextAmendment, savedWorkflow)) {
          const delayedWorkflow = {
            ...savedWorkflow,
            resumeAfter:
              Date.now() + resumeDelayForState(savedWorkflow.lastKnownState),
          };
          if (persistWorkflow(delayedWorkflow)) setWorkflow(delayedWorkflow);
          setError(
            "The amendment response did not match this reservation and saved date request. No completion was reported.",
          );
          setMessage("");
          return;
        }
        const nextWorkflow = {
          ...savedWorkflow,
          amendmentId: nextAmendment.id,
          lastKnownState: ACTIVE_STATES.has(nextAmendment.state)
            ? nextAmendment.state
            : null,
          requiresManualReconciliation:
            nextAmendment.requiresManualReconciliation === true,
          resumeAfter:
            Date.now() + resumeDelayForState(nextAmendment.state),
        };
        setAmendment(nextAmendment);

        if (nextAmendment.state === "succeeded") {
          clearPoll();
          removeWorkflow(savedWorkflow.bookingId);
          quoteRequestRef.current += 1;
          setQuote(null);
          setWorkflow(null);
          setMessage("Reservation dates and linked refund completed successfully.");
          if (completedIdRef.current !== nextAmendment.id) {
            completedIdRef.current = nextAmendment.id;
            if (typeof onCompleted === "function") onCompleted(nextAmendment);
          }
          return;
        }

        if (nextAmendment.state === "terminal_failed") {
          clearPoll();
          removeWorkflow(savedWorkflow.bookingId);
          quoteRequestRef.current += 1;
          setQuote(null);
          setWorkflow(null);
          setMessage("");
          setError(
            nextAmendment.lastError && nextAmendment.lastError.message
              ? `${nextAmendment.lastError.message}${
                  nextAmendment.lastError.code
                    ? ` (${nextAmendment.lastError.code})`
                    : ""
                }`
              : "The reservation amendment could not be completed.",
          );
          return;
        }

        if (!persistWorkflow(nextWorkflow)) {
          setError(
            "The amendment status could not be saved safely. Keep this dialog open and do not submit another request.",
          );
          setMessage("");
          return;
        }
        setWorkflow(nextWorkflow);

        setMessage(
          nextAmendment.state === "retryable_failed"
            ? "A background retry is scheduled. Automatic checks are paused to preserve server backoff; use the same-key status button later."
            : nextAmendment.state === "reconciling" ||
                nextAmendment.requiresManualReconciliation
            ? "Manual reconciliation is required. Automatic checks are paused; do not submit another amendment. Use the same-key status button only when checking again."
            : "The amendment is still processing. Status will refresh automatically.",
        );
        scheduleReplay(nextWorkflow, nextAmendment, token);
      } catch (requestError) {
        if (!isCurrentContext(token, savedWorkflow.bookingId)) return;
        const responseAmendment =
          requestError &&
          (requestError.amendment || requestError.data?.amendment);
        if (
          requestError &&
          requestError.code === "reservation_amendment_in_progress"
        ) {
          removeWorkflow(savedWorkflow.bookingId);
          quoteRequestRef.current += 1;
          setQuote(null);
          setWorkflow(null);
          setAmendment(null);
          setError(
            "Another durable amendment is already active for this reservation. This request was not created; wait for the existing operation to finish before requesting a fresh quote. (reservation_amendment_in_progress)",
          );
          setMessage("");
          return;
        }
        if (responseAmendment) {
          if (!amendmentMatchesWorkflow(responseAmendment, savedWorkflow)) {
            const delayedWorkflow = {
              ...savedWorkflow,
              resumeAfter:
                Date.now() + resumeDelayForState(savedWorkflow.lastKnownState),
            };
            if (persistWorkflow(delayedWorkflow)) setWorkflow(delayedWorkflow);
            setError(
              "The amendment error response did not match this reservation and saved date request. The saved operation key was retained.",
            );
            setMessage("");
            return;
          }
          setAmendment(responseAmendment);
          if (responseAmendment.state === "terminal_failed") {
            removeWorkflow(savedWorkflow.bookingId);
            quoteRequestRef.current += 1;
            setQuote(null);
            setWorkflow(null);
            setMessage("");
            setError(errorMessage(requestError, "The amendment could not be completed."));
            return;
          }
        }
        const safelyRejectedBeforeCreation = Boolean(
          !responseAmendment &&
            Number(requestError && requestError.statusCode) >= 400 &&
            Number(requestError && requestError.statusCode) < 500 &&
            SAFE_PRE_OPERATION_REJECTION_CODES.has(requestError && requestError.code),
        );
        setCanClearRejectedWorkflow(safelyRejectedBeforeCreation);
        if (!safelyRejectedBeforeCreation) {
          const delayedWorkflow = {
            ...savedWorkflow,
            resumeAfter:
              Date.now() + resumeDelayForState(savedWorkflow.lastKnownState),
          };
          if (persistWorkflow(delayedWorkflow)) setWorkflow(delayedWorkflow);
        }
        setError(
          errorMessage(
            requestError,
            "The request outcome is unknown. Resume the saved request with the same operation key.",
          ),
        );
        setMessage(
          "The saved operation key was retained. Resume this request; do not create another amendment.",
        );
      } finally {
        if (isCurrentContext(token, savedWorkflow.bookingId)) {
          submitLockRef.current = false;
          setSubmitting(false);
        }
      }
    },
    [clearPoll, isCurrentContext, onCompleted, scheduleReplay],
  );

  runWorkflowRef.current = runWorkflow;

  useEffect(() => {
    contextRef.current += 1;
    const token = contextRef.current;
    clearPoll();
    clearResumeUnlock();
    submitLockRef.current = false;
    completedIdRef.current = null;
    quoteRequestRef.current += 1;
    setQuote(null);
    setAmendment(null);
    setError("");
    setMessage("");
    setLoadingQuote(false);
    setSubmitting(false);
    setCanClearRejectedWorkflow(false);

    if (!isOpen || !bookingId) {
      setWorkflow(null);
      return () => {
        clearPoll();
        clearResumeUnlock();
      };
    }

    const savedWorkflow = readWorkflow(bookingId);
    if (savedWorkflow) {
      setProposedCheckInDate(savedWorkflow.proposedCheckInDate);
      setProposedCheckOutDate(savedWorkflow.proposedCheckOutDate);
      setReason(savedWorkflow.reason);
      setNoticeWaiver(true);
      setWorkflow(savedWorkflow);
      if (
        savedWorkflow.lastKnownState === "retryable_failed" ||
        savedWorkflow.lastKnownState === "reconciling" ||
        savedWorkflow.requiresManualReconciliation
      ) {
        setMessage(
          savedWorkflow.lastKnownState === "retryable_failed"
            ? "A saved amendment has a background retry scheduled. Automatic replay remains paused; use the same-key status button later."
            : "A saved amendment requires reconciliation. Automatic replay remains paused; do not submit another request.",
        );
      } else {
        const delay = Math.max(0, savedWorkflow.resumeAfter - Date.now());
        if (delay > 0) {
          setMessage(
            "The saved amendment will replay with its original operation key after the server retry interval.",
          );
          pollTimerRef.current = setTimeout(() => {
            if (runWorkflowRef.current) {
              runWorkflowRef.current(savedWorkflow, token, {
                automatic: true,
              });
            }
          }, delay);
        } else {
          setMessage("Resuming the saved amendment request with its original operation key...");
          runWorkflowRef.current(savedWorkflow, token, { automatic: true });
        }
      }
    } else {
      setProposedCheckInDate(initialCheckIn);
      setProposedCheckOutDate(initialCheckOut);
      setReason("");
      setNoticeWaiver(false);
      setWorkflow(null);
    }

    return () => {
      clearPoll();
      clearResumeUnlock();
    };
  }, [
    bookingId,
    clearPoll,
    clearResumeUnlock,
    initialCheckIn,
    initialCheckOut,
    isOpen,
  ]);

  const resumeAfter = workflow ? workflow.resumeAfter : 0;

  useEffect(() => {
    clearResumeUnlock();
    setResumeClock(Date.now());
    if (!isOpen || !workflow || resumeAfter <= Date.now()) return undefined;
    resumeUnlockTimerRef.current = setTimeout(() => {
      setResumeClock(resumeAfter);
      resumeUnlockTimerRef.current = null;
    }, resumeAfter - Date.now());
    return () => clearResumeUnlock();
  }, [clearResumeUnlock, isOpen, resumeAfter, workflow]);

  const currentRequest = useMemo(
    () =>
      requestFromState(
        bookingId,
        proposedCheckInDate,
        proposedCheckOutDate,
        reason,
        noticeWaiver,
      ),
    [bookingId, noticeWaiver, proposedCheckInDate, proposedCheckOutDate, reason],
  );

  const previewIsCurrent = Boolean(
    currentRequest.value &&
      quote &&
      quoteMatchesRequest(quote, currentRequest.value),
  );
  const canSubmit = Boolean(
    previewIsCurrent && quoteCanCommit(quote) && !workflow && !submitting,
  );

  const invalidatePreview = () => {
    quoteRequestRef.current += 1;
    setQuote(null);
    setError("");
    setMessage("");
  };

  const handlePreview = async (event) => {
    event.preventDefault();
    if (workflow) {
      setError("Finish or resolve the saved amendment before quoting another date change.");
      return;
    }
    if (!currentRequest.value) {
      setError(currentRequest.error);
      return;
    }
    const requestNumber = quoteRequestRef.current + 1;
    quoteRequestRef.current = requestNumber;
    const token = contextRef.current;
    setLoadingQuote(true);
    setQuote(null);
    setError("");
    setMessage("");
    try {
      const nextQuote = await reservationAmendmentService.quote(currentRequest.value);
      if (
        !isCurrentContext(token, currentRequest.value.bookingId) ||
        quoteRequestRef.current !== requestNumber
      ) {
        return;
      }
      if (!quoteMatchesRequest(nextQuote, currentRequest.value)) {
        setError(
          "The amendment quote did not match this reservation and proposed date request.",
        );
        return;
      }
      setQuote(nextQuote);
      setMessage(
        quoteCanCommit(nextQuote)
          ? "Review every effect below before confirming."
          : nextQuote.blockMessage ||
              "This date change needs a workflow that is outside this branch.",
      );
    } catch (quoteError) {
      if (
        isCurrentContext(token, currentRequest.value.bookingId) &&
        quoteRequestRef.current === requestNumber
      ) {
        setError(errorMessage(quoteError, "The amendment quote could not be loaded."));
      }
    } finally {
      if (
        isCurrentContext(token, currentRequest.value.bookingId) &&
        quoteRequestRef.current === requestNumber
      ) {
        setLoadingQuote(false);
      }
    }
  };

  const handleCommit = async () => {
    if (submitLockRef.current || submitting) return;
    if (!canSubmit || !currentRequest.value) {
      setError("Refresh and review a committable amendment quote before confirming.");
      return;
    }
    let idempotencyKey;
    try {
      idempotencyKey = reservationAmendmentService.createOperationKey();
    } catch (keyError) {
      setError(errorMessage(keyError, "A secure amendment key could not be created."));
      return;
    }
    const nextWorkflow = {
      version: WORKFLOW_VERSION,
      ...currentRequest.value,
      quoteFingerprint: quote.quoteFingerprint,
      idempotencyKey,
      amendmentId: null,
      lastKnownState: null,
      requiresManualReconciliation: false,
      resumeAfter: 0,
    };
    submitLockRef.current = true;
    if (!persistWorkflow(nextWorkflow)) {
      submitLockRef.current = false;
      setError(
        "The amendment request could not be saved safely. No refund or date change was submitted.",
      );
      return;
    }
    setWorkflow(nextWorkflow);
    await runWorkflow(nextWorkflow, contextRef.current);
  };

  const handleResume = () => {
    if (!workflow || submitLockRef.current || submitting) return;
    if (resumeClock < workflow.resumeAfter) {
      setError(
        `The progress command is paused for ${waitLabel(
          workflow.resumeAfter - resumeClock,
        )} to preserve server retry backoff.`,
      );
      return;
    }
    runWorkflow(workflow, contextRef.current);
  };

  const handleDiscard = () => {
    if (!workflow || submitting || amendment || !canClearRejectedWorkflow) return;
    removeWorkflow(bookingId);
    quoteRequestRef.current += 1;
    setQuote(null);
    setWorkflow(null);
    setCanClearRejectedWorkflow(false);
    setError("");
    setMessage("The unconfirmed saved request was discarded. Request a fresh quote before continuing.");
  };

  const statusIsActive = Boolean(amendment && ACTIVE_STATES.has(amendment.state));
  const effectiveStatus = quote ? quote.status : bookingStatus;
  const resumeIsDue = Boolean(
    workflow && resumeClock >= workflow.resumeAfter,
  );

  return (
    <Modal
      isOpen={Boolean(isOpen)}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Amend reservation dates"
      ariaHideApp={false}
      shouldCloseOnEsc={!submitting}
      shouldCloseOnOverlayClick={!submitting}
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div>
          <h2 className="tw-m-0 tw-text-xl tw-font-semibold">Amend reservation dates</h2>
          <p className="tw-mt-1 tw-text-sm tw-text-gray-600">
            Reservation {reservationId || bookingId || ""}
            {effectiveStatus ? ` · ${effectiveStatus}` : ""}
            {bookingType ? ` · ${bookingType}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={submitting ? undefined : onClose}
          disabled={submitting}
          className="tw-border-0 tw-bg-transparent tw-text-2xl tw-leading-none tw-text-gray-600"
          aria-label="Close amendment dialog"
        >
          ×
        </button>
      </div>

      {statusIsActive ? (
        <div className="tw-mt-4 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-p-3">
          <strong>{stateLabel(amendment)}</strong>
          <p className="tw-mb-0 tw-mt-1 tw-text-sm">
            {amendment.requiresManualReconciliation
              ? "Manual review is required. Do not submit another amendment."
              : "This durable request is not complete yet. Do not submit another amendment."}
          </p>
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="tw-mt-4 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-800">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="tw-mt-4 tw-rounded tw-border tw-border-blue-100 tw-bg-blue-50 tw-p-3 tw-text-sm tw-text-blue-900">
          {message}
        </div>
      ) : null}

      <form onSubmit={handlePreview} className="tw-mt-5">
        <fieldset disabled={Boolean(workflow) || submitting} className="tw-border-0 tw-p-0">
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
            <label className="tw-text-sm tw-font-semibold">
              Proposed check-in
              <input
                aria-label="Proposed check-in"
                type="date"
                value={proposedCheckInDate}
                onChange={(event) => {
                  setProposedCheckInDate(event.target.value);
                  invalidatePreview();
                }}
                className="form-control tw-mt-1"
                required
              />
            </label>
            <label className="tw-text-sm tw-font-semibold">
              Proposed check-out
              <input
                aria-label="Proposed check-out"
                type="date"
                value={proposedCheckOutDate}
                onChange={(event) => {
                  setProposedCheckOutDate(event.target.value);
                  invalidatePreview();
                }}
                className="form-control tw-mt-1"
                required
              />
            </label>
          </div>

          <label className="tw-mt-4 tw-block tw-text-sm tw-font-semibold">
            Administrative reason
            <textarea
              aria-label="Administrative reason"
              value={reason}
              maxLength={MAX_REASON_LENGTH}
              onChange={(event) => {
                setReason(event.target.value);
                invalidatePreview();
              }}
              rows={3}
              className="form-control tw-mt-1"
              placeholder="Describe the guest and host agreement and why the dates are changing."
              required
            />
          </label>
          <div className="tw-mt-1 tw-text-right tw-text-xs tw-text-gray-500">
            {reason.length}/{MAX_REASON_LENGTH}
          </div>

          <label className="tw-mt-4 tw-flex tw-items-start tw-gap-2 tw-text-sm">
            <input
              aria-label="Guest and host agreement"
              type="checkbox"
              checked={noticeWaiver}
              onChange={(event) => {
                setNoticeWaiver(event.target.checked);
                invalidatePreview();
              }}
              className="tw-mt-1"
            />
            <span>
              I confirm the guest and host mutually agreed to this change and authorize the notice waiver.
            </span>
          </label>

          <button
            type="submit"
            disabled={loadingQuote}
            className="btn btn-secondary tw-mt-4"
          >
            {loadingQuote ? "Calculating..." : "Preview date amendment"}
          </button>
        </fieldset>
      </form>

      {quote ? (
        <section className="tw-mt-5" aria-label="Amendment preview">
          <h3 className="tw-text-lg tw-font-semibold">Authoritative preview</h3>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
            <div className="tw-rounded tw-border tw-border-gray-200 tw-p-3">
              <InfoRow label="Current stay" value={`${quote.previousCheckInDate} → ${quote.previousCheckOutDate}`} />
              <InfoRow label="Proposed stay" value={`${quote.proposedCheckInDate} → ${quote.proposedCheckOutDate}`} />
              <InfoRow label="Change type" value={quote.classification.replaceAll("_", " ")} />
              <InfoRow
                label="Availability"
                value={quote.availability.available ? "Available" : "Conflict found"}
                warning={!quote.availability.available}
              />
              <InfoRow label="Required action" value={actionLabel(quote.financialAction)} warning={!quoteCanCommit(quote)} />
              <InfoRow label="Business timezone" value={quote.timezone} />
            </div>
            <div className="tw-rounded tw-border tw-border-gray-200 tw-p-3">
              {quote.financialAction === "exact_refund" ? (
                <>
                  <InfoRow label="Guest price difference" value={formatCents(quote.guestPriceDifferenceCents, quote.currency)} />
                  <InfoRow label="Guest refund" value={formatCents(quote.guestRefundCents, quote.currency)} />
                  <InfoRow label="Host earnings reversed" value={formatCents(quote.hostDebitCents, quote.currency)} />
                  <InfoRow label="Future host earnings prevented" value={formatCents(quote.preventedFutureHostEarningsCents, quote.currency)} />
                  <InfoRow label="Platform revenue reversal" value={formatCents(quote.platformRevenueReversalCents, quote.currency)} />
                  <InfoRow
                    label="Processing fee impact"
                    value={
                      quote.refundQuote &&
                      Number.isSafeInteger(quote.refundQuote.processingFeeImpactCents)
                        ? formatCents(quote.refundQuote.processingFeeImpactCents, quote.currency)
                        : "Unavailable"
                    }
                  />
                </>
              ) : (
                <p className="tw-mb-0 tw-text-sm tw-text-amber-900">
                  No price, charge, refund, or earnings change was calculated or submitted for this unsupported action.
                </p>
              )}
            </div>
          </div>

          <div className="tw-mt-4 tw-rounded tw-border tw-border-gray-200 tw-p-3 tw-text-sm">
            <p><strong>Removed service dates:</strong> {dateListLabel(quote.removedServiceDates)}</p>
            <p><strong>Added service dates:</strong> {dateListLabel(quote.addedServiceDates)}</p>
            <p className="tw-mb-0"><strong>Retained service dates:</strong> {dateListLabel(quote.retainedServiceDates)}</p>
          </div>

          {quote.availability.conflicts.length > 0 ? (
            <div className="tw-mt-3 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm">
              <strong>Reservation conflicts</strong>
              <ul className="tw-mb-0 tw-mt-2 tw-pl-5">
                {quote.availability.conflicts.map((conflict, index) => (
                  <li key={conflict.id || `${conflict.bookingId || "conflict"}-${index}`}>
                    {conflict.bookingId || conflict.id || "Reservation"}: {dateOnly(conflict.checkInDate) || "?"} → {dateOnly(conflict.checkOutDate) || "?"}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {quote.availability.manualBlocks.length > 0 ? (
            <div className="tw-mt-3 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm">
              <strong>Manual availability blocks</strong>
              <ul className="tw-mb-0 tw-mt-2 tw-pl-5">
                {quote.availability.manualBlocks.map((block, index) => (
                  <li key={`${block.type || "block"}-${block.serviceDate || index}`}>
                    {block.type || "Block"}: {block.serviceDate || "?"}{block.throughDate ? ` through ${block.throughDate}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {!quoteCanCommit(quote) ? (
            <div className="tw-mt-3 tw-rounded tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-900">
              <strong>This preview cannot be confirmed here.</strong>{" "}
              {quote.blockMessage ||
                "The required financial action is outside the supported exact-refund workflow."}
              {quote.financialAction === "customer_authorization_required" ? (
                <span> Obtain explicit customer payment authorization before adding dates.</span>
              ) : null}
              {quote.financialAction === "invoice_workflow_required" ? (
                <span> Use a separately designed invoice or payment-request workflow.</span>
              ) : null}
            </div>
          ) : null}
          {quote.financialAction === "exact_refund" &&
          quote.refundQuote &&
          Array.isArray(quote.refundQuote.dataQualityCodes) &&
          quote.refundQuote.dataQualityCodes.length > 0 ? (
            <div className="tw-mt-3 tw-rounded tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-900">
              <strong>Financial data notes:</strong>{" "}
              {quote.refundQuote.dataQualityCodes.join(", ")}
            </div>
          ) : null}
        </section>
      ) : null}

      {amendment ? (
        <section className="tw-mt-5 tw-rounded tw-border tw-border-gray-200 tw-p-3" aria-label="Amendment status">
          <h3 className="tw-text-base tw-font-semibold">Saved operation status</h3>
          <InfoRow label="State" value={stateLabel(amendment)} warning={amendment.state === "terminal_failed" || amendment.requiresManualReconciliation} />
          <InfoRow label="Operation" value={amendment.id} />
          <InfoRow label="Linked refund" value={amendment.refundOperationId || "Not linked yet"} />
        </section>
      ) : null}

      <div className="tw-mt-5 tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
        <button
          type="button"
          onClick={submitting ? undefined : onClose}
          disabled={submitting}
          className="btn btn-light"
        >
          Close
        </button>
        {workflow && !submitting && !canClearRejectedWorkflow ? (
          <button
            type="button"
            onClick={handleResume}
            disabled={!resumeIsDue}
            className="btn btn-secondary"
          >
            {resumeIsDue
              ? "Run saved progress command"
              : `Progress command paused (${waitLabel(
                  workflow.resumeAfter - resumeClock,
                )})`}
          </button>
        ) : null}
        {workflow && !submitting && !amendment && canClearRejectedWorkflow ? (
          <button type="button" onClick={handleDiscard} className="btn btn-outline-danger">
            Clear server-rejected request
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleCommit}
          disabled={!canSubmit}
          className="btn btn-primary"
        >
          {submitting ? "Processing..." : "Confirm date amendment"}
        </button>
      </div>
    </Modal>
  );
}

export const reservationAmendmentUiPrivate = {
  actionLabel,
  amendmentMatchesWorkflow,
  dateOnly,
  formatCents,
  quoteCanCommit,
  quoteMatchesRequest,
  stateLabel,
  validatedWorkflow,
};
